/**
 * Design Tokens – Single Source of Truth
 * 
 * All theme-related constants live here. Components import from this file
 * instead of hardcoding values. CSS custom properties are referenced with
 * fallbacks so the app works even without a client style.css loaded.
 * 
 * SOLID: Single Responsibility – this module owns design token resolution.
 * DRY: Every repeated value is defined exactly once.
 */

// ─── Color Tokens (CSS variable references with fallbacks) ─────────────────

export const colors = {
  primary:     'var(--color-primary, #6B00D6)',
  primaryDark: 'var(--color-primary-dark, #4A0099)',
  primaryLight:'var(--color-primary-light, #F3E8FF)',

  accent:      'var(--color-accent, #00E5A0)',
  accentDark:  'var(--color-accent-dark, #00CC8E)',
  accentInfo:  'var(--color-accent-info, #00BFFF)',
  accentInfoStrong: 'var(--color-accent-info-strong, #00D4FF)',
  purpleGlow:  'var(--color-purple-glow, #7300FF)',

  bgLight:     'var(--color-bg-light, #F8F8FF)',
  bgDark:      'var(--color-bg-dark, #1a0040)',
  surfaceBase:   'var(--color-surface-base, #FFF)',
  surfaceMuted:  'var(--color-surface-muted, #F5F5F5)',
  surfaceSubtle: 'var(--color-surface-subtle, #FAFAFA)',
  surfaceHover:  'var(--color-surface-hover, #F0F0F0)',

  textPrimary:   'var(--color-text-primary, #1a1a1a)',
  textSecondary: 'var(--color-text-secondary, #666)',

  // Neutral palette (used across labels, cards, etc.)
  neutral900: 'var(--color-neutral-900, #000)',
  neutral800: 'var(--color-neutral-800, #1A1A1A)',
  neutral750: 'var(--color-neutral-750, #2E2E2E)',
  neutral700: 'var(--color-neutral-700, #323232)',
  neutral650: 'var(--color-neutral-650, #404040)',
  neutral600: 'var(--color-neutral-600, #4E4E4E)',
  neutral500: 'var(--color-neutral-500, #585858)',
  neutral450: 'var(--color-neutral-450, #5F5F5F)',
  neutral400: 'var(--color-neutral-400, #666)',
  neutral350: 'var(--color-neutral-350, #6C6C6C)',
  neutral300: 'var(--color-neutral-300, #888)',
  neutral250: 'var(--color-neutral-250, #878787)',
  neutral200: 'var(--color-neutral-200, #A0A0A0)',
  neutral150: 'var(--color-neutral-150, #B5B5B5)',
  neutral100: 'var(--color-neutral-100, #EAEAEA)',
  neutral90:  'var(--color-neutral-90, #E2E2E2)',
  neutral80:  'var(--color-neutral-80, #F0F0F0)',
  neutral50:  'var(--color-neutral-50, #F5F5F5)',

  whatsapp: 'var(--color-whatsapp, #25D366)',
  whatsappDark: 'var(--color-whatsapp-dark, #1EBE5A)',
  waze: 'var(--color-waze, #33CCFF)',
  ratingStar: 'var(--color-rating-star, #FFB800)',
  avatarLight: 'var(--color-avatar-light, #E0E0E0)',
  avatarDark: 'var(--color-avatar-dark, #BDBDBD)',
  borderLight: 'var(--color-border-light, #EAEAEA)',
  borderSoft: 'var(--color-border-soft, #F0F0F0)',
  divider: 'var(--color-divider, #E2E2E2)',

  overlayLabel: '#5F5F5F1A',
  overlayWhite05: '#FFFFFF0D',
  overlayWhite10: '#FFFFFF1A',
  overlayWhite15: '#FFFFFF26',
  overlayWhite20: '#FFFFFF33',
  overlayWhite25: '#FFFFFF40',
  overlayWhite40: '#FFFFFF66',
  overlayWhite50: '#FFFFFF80',
  overlayWhite60: '#FFFFFF99',
  overlayWhite70: '#FFFFFFB3',
  overlayWhite80: '#FFFFFFCC',
  overlayBlack04: '#0000000A',
  overlayBlack05: '#0000000D',
  overlayBlack15: '#00000026',
  overlayBlack21: '#00000036',
  overlayBlack25: '#00000040',
  overlayBlack30: '#0000004D',
  overlayBlack35: '#00000059',
  overlayBlack40: '#00000066',
  overlayBlack45: '#00000073',
  shadowCyan: '#00BFFF4D',
  shadowWhatsapp: '#25D36666',
  shadowWhatsappHover: '#25D36680',
  white: 'var(--color-white, #FFF)',
} as const;

// ─── Typography ────────────────────────────────────────────────────────────

export const fontFamily = "'Figtree', sans-serif";

// ─── Shared Component Styles (DRY) ─────────────────────────────────────────

/** The small pill-shaped category/label tag used in FAQ, Features, AppSection, etc. */
export const labelTagSx = {
  borderRadius: '0.5625rem',
  background: colors.overlayLabel,
  color: colors.neutral450,
  fontFamily,
  fontSize: '0.625rem',
  fontWeight: 600,
  px: 1.5,
  py: 0.5,
  width: 'fit-content',
} as const;

/** The white-on-dark variant of the label tag (used inside purple cards like "Sobre Nós") */
export const labelTagDarkSx = {
  ...labelTagSx,
  background: colors.overlayWhite10,
  color: colors.white,
} as const;

// ─── Border Radius ─────────────────────────────────────────────────────────

export const radius = {
  sm:   'var(--radius-sm, 8px)',
  md:   'var(--radius-md, 16px)',
  lg:   'var(--radius-lg, 24px)',
  full: 'var(--radius-full, 9999px)',
  card: '1.5625rem',   // 25px – standard card radius from Figma
  pill: '0.5625rem',   // 9px  – label pill radius
} as const;
